import api from './api';

const BASE_URL = '/change-requests';

// Get all change requests with filtering
export const getChangeRequests = async (params = {}) => {
  const response = await api.get(BASE_URL, { params });
  return response.data;
};

// Get single change request
export const getChangeRequest = async (id) => {
  const response = await api.get(`${BASE_URL}/${id}`);
  return response.data;
};

// Create new change request
export const createChangeRequest = async (data) => {
  const response = await api.post(BASE_URL, data);
  return response.data;
};

// Update change request
export const updateChangeRequest = async (id, data) => {
  const response = await api.put(`${BASE_URL}/${id}`, data);
  return response.data;
};

// Delete change request
export const deleteChangeRequest = async (id) => {
  const response = await api.delete(`${BASE_URL}/${id}`);
  return response.data;
};

// Submit change request for review
export const submitChangeRequest = async (id) => {
  const response = await api.post(`${BASE_URL}/${id}/submit`);
  return response.data;
};

// Review change request
export const reviewChangeRequest = async (id, reviewComments) => {
  const response = await api.post(`${BASE_URL}/${id}/review`, {
    review_comments: reviewComments
  });
  return response.data;
};

// Approve change request
export const approveChangeRequest = async (id, approvalComments) => {
  const response = await api.post(`${BASE_URL}/${id}/approve`, {
    approval_comments: approvalComments
  });
  return response.data;
};

// Reject change request
export const rejectChangeRequest = async (id, rejectionReason) => {
  const response = await api.post(`${BASE_URL}/${id}/reject`, {
    rejection_reason: rejectionReason
  });
  return response.data;
};

// Mark change request as implemented
export const implementChangeRequest = async (id, data = {}) => {
  const response = await api.post(`${BASE_URL}/${id}/implement`, data);
  return response.data;
};

// Cancel change request
export const cancelChangeRequest = async (id) => {
  const response = await api.post(`${BASE_URL}/${id}/cancel`);
  return response.data;
};

// Get change request statistics
export const getChangeRequestStats = async (projectId = null) => {
  const params = projectId ? { project_id: projectId } : {};
  const response = await api.get(`${BASE_URL}/stats`, { params });
  return response.data;
};

// Helper functions for filtering and formatting

export const getStatusColor = (status) => {
  const colors = {
    'Draft': 'badge badge-secondary',
    'Submitted': 'badge badge-primary',
    'Under Review': 'badge badge-warning',
    'Approved': 'badge badge-success',
    'Rejected': 'badge badge-danger',
    'Implemented': 'badge badge-info',
    'Cancelled': 'badge badge-secondary'
  };
  return colors[status] || 'badge badge-secondary';
};

export const getStatusBadgeClass = (status) => {
  const classes = {
    'Draft': 'badge badge-secondary',
    'Submitted': 'badge badge-primary',
    'Under Review': 'badge badge-warning',
    'Approved': 'badge badge-success',
    'Rejected': 'badge badge-danger',
    'Implemented': 'badge badge-info',
    'Cancelled': 'badge badge-secondary'
  };
  return classes[status] || 'badge badge-secondary';
};

export const getPriorityColor = (priority) => {
  const colors = {
    'Low': 'badge badge-success',
    'Medium': 'badge badge-warning',
    'High': 'badge badge-primary',
    'Critical': 'badge badge-danger'
  };
  return colors[priority] || 'badge badge-secondary';
};

export const getPriorityBadgeClass = (priority) => {
const classes = {
  'Low': 'badge badge-success',
  'Medium': 'badge badge-warning',
  'High': 'badge badge-primary',
  'Critical': 'badge badge-danger'
};

return classes[priority] || 'badge badge-secondary';
};

export const getImpactLevelColor = (level) => {
const colors = {
  Low: 'badge badge-success',
  Medium: 'badge badge-warning',
  High: 'badge badge-primary',
  Critical: 'badge badge-danger'
};

return colors[level] || 'badge badge-secondary';
};

export const getImpactLevelBadgeClass = (level) => {
  const classes = {
    'Low': 'badge badge-success',
    'Medium': 'badge badge-warning',
    'High': 'badge badge-primary',
    'Critical': 'badge badge-danger'
  };
  return classes[level] || 'badge badge-secondary';
};

export const getChangeTypeIcon = (type) => {
  const icons = {
    'Scope': '📋',
    'Schedule': '📅',
    'Budget': '💰',
    'Resource': '👥',
    'Quality': '⭐',
    'Risk': '⚠️',
    'Other': '📝'
  };
  return icons[type] || '📝';
};

export const formatCurrency = (amount) => {
  if (!amount) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

export const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatDateTime = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const canEdit = (changeRequest, currentUser) => {
  if (!changeRequest || !currentUser) return false;
  
  // Can edit if status is Draft or Submitted and user is the requester
  if (['Draft', 'Submitted'].includes(changeRequest.status)) {
    return changeRequest.requested_by === currentUser.id;
  }
  
  return false;
};

export const canSubmit = (changeRequest, currentUser) => {
  if (!changeRequest || !currentUser) return false;
  
  // Can submit if status is Draft and user is the requester
  return changeRequest.status === 'Draft' && 
         changeRequest.requested_by === currentUser.id;
};

export const canReview = (changeRequest, currentUser) => {
  if (!changeRequest || !currentUser) return false;
  
  // Can review if status is Submitted
  // In a real app, you'd check for specific permissions/roles
  return changeRequest.status === 'Submitted';
};

export const canApprove = (changeRequest, currentUser) => {
  if (!changeRequest || !currentUser) return false;
  
  // Can approve if status is Submitted or Under Review
  // In a real app, you'd check for specific permissions/roles
  return ['Submitted', 'Under Review'].includes(changeRequest.status);
};

export const canReject = (changeRequest, currentUser) => {
  if (!changeRequest || !currentUser) return false;
  
  // Can reject if status is Submitted or Under Review
  // In a real app, you'd check for specific permissions/roles
  return ['Submitted', 'Under Review'].includes(changeRequest.status);
};

export const canImplement = (changeRequest, currentUser) => {
  if (!changeRequest || !currentUser) return false;
  
  // Can implement if status is Approved
  return changeRequest.status === 'Approved';
};

export const canCancel = (changeRequest, currentUser) => {
  if (!changeRequest || !currentUser) return false;
  
  // Can cancel if not already Implemented or Cancelled
  return !['Implemented', 'Cancelled'].includes(changeRequest.status) &&
         changeRequest.requested_by === currentUser.id;
};

export const canDelete = (changeRequest, currentUser) => {
  if (!changeRequest || !currentUser) return false;
  
  // Can delete if status is Draft or Cancelled and user is the requester
  return ['Draft', 'Cancelled'].includes(changeRequest.status) &&
         changeRequest.requested_by === currentUser.id;
};

// Validation helpers
export const validateChangeRequest = (data) => {
  const errors = {};
  
  if (!data.project_id) {
    errors.project_id = 'Project is required';
  }
  
  if (!data.title || data.title.trim() === '') {
    errors.title = 'Title is required';
  }
  
  if (!data.description || data.description.trim() === '') {
    errors.description = 'Description is required';
  }
  
  if (!data.change_type) {
    errors.change_type = 'Change type is required';
  }
  
  if (!data.priority) {
    errors.priority = 'Priority is required';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export default {
  getChangeRequests,
  getChangeRequest,
  createChangeRequest,
  updateChangeRequest,
  deleteChangeRequest,
  submitChangeRequest,
  reviewChangeRequest,
  approveChangeRequest,
  rejectChangeRequest,
  implementChangeRequest,
  cancelChangeRequest,
  getChangeRequestStats,
  getStatusColor,
  getStatusBadgeClass,
  getPriorityColor,
  getPriorityBadgeClass,
  getImpactLevelColor,
  getImpactLevelBadgeClass,
  getChangeTypeIcon,
  formatCurrency,
  formatDate,
  formatDateTime,
  canEdit,
  canSubmit,
  canReview,
  canApprove,
  canReject,
  canImplement,
  canCancel,
  canDelete,
  validateChangeRequest
};


